import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ROTULO_TIPO_MARCACAO, type HoraACobrar, type HorasACobrar } from '@arena/shared/schemas';
import { hojeNoFuso, partesNoFuso } from '@arena/shared/tempo';
import { agendaApi } from '../api/agenda.js';
import { mensagemDoErro } from '../api/cliente.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, Esqueleto } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { Botao, Card, Feed, ItemDeFeed, Status, Tag, Vazio } from '../ui/primitivos.jsx';
import { dataCurta, dinheiro, horaCurta, horaNaArena, horas, telefoneFormatado } from './formatos.js';
import { ModalDeBaixa } from './ModalDeBaixa.jsx';

/**
 * "Horas a cobrar" — a ponte entre a agenda e o caixa.
 *
 * A pergunta que isto responde é literal: *quais horários estão em aberto
 * para eu fechar o aluguel?* Antes dela a resposta era abrir a grade e
 * conferir card por card qual selo dizia "a pagar".
 *
 * Um componente, três lugares — painel de hoje, financeiro e o
 * drill-down do KPI "Previsto de locação" —, porque é a mesma lista com
 * o mesmo clique: abrir a baixa. Repetir a lista em cada tela faria três
 * versões do critério de "em aberto" na primeira mudança.
 *
 * O que ela NÃO faz é somar. O total vem do servidor, que é quem sabe o
 * recorte inteiro; a tela somando o que recebeu mostraria o total da
 * página, não o do período.
 */

export function CardDeHorasACobrar({
  titulo,
  de,
  ate,
  dica,
  acao,
  /** Quantos itens antes do "ver mais". Sem limite, todos. */
  limite,
  vazio = 'Nenhuma hora a cobrar no período.',
}: {
  titulo: string;
  de?: string;
  ate?: string;
  dica?: string;
  acao?: React.ReactNode;
  limite?: number;
  vazio?: string;
}) {
  const consulta = useConsultaDeHorasACobrar({ de, ate });

  if (consulta.isPending) {
    return (
      <Card titulo={titulo} dica={dica}>
        <div style={{ display: 'grid', gap: 12 }} aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Esqueleto key={i} altura={36} />
          ))}
        </div>
      </Card>
    );
  }

  if (consulta.isError) {
    return (
      <Card titulo={titulo} dica={dica}>
        <Erro
          detalhe={mensagemDoErro(consulta.error)}
          onTentarDeNovo={() => void consulta.refetch()}
        />
      </Card>
    );
  }

  const dados = consulta.data;
  return (
    <Card titulo={titulo} dica={dica ?? resumoDoPeriodo(dados)} acao={acao}>
      <ResumoDeHorasACobrar dados={dados} />
      <ListaDeHorasACobrar dados={dados} limite={limite} vazio={vazio} />
    </Card>
  );
}

/** A consulta, exposta para quem precisa dos totais sem o card. */
export function useConsultaDeHorasACobrar(periodo: { de?: string; ate?: string }) {
  const { pode } = useSessao();
  return useQuery({
    queryKey: ['agenda', 'a-cobrar', periodo.de ?? 'hoje', periodo.ate ?? periodo.de ?? 'hoje'],
    queryFn: () => agendaApi.aCobrar(periodo),
    // Cosmético, como todo `pode()` do front: quem decide é o servidor,
    // que devolve 403 a quem não enxerga valor.
    enabled: pode('agenda', 'ler'),
  });
}

function resumoDoPeriodo(dados: HorasACobrar): string {
  return dados.de === dados.ate
    ? dataCurta(dados.de)
    : `${dataCurta(dados.de)} a ${dataCurta(dados.ate)}`;
}

/**
 * As duas linhas de total: o que falta receber e o que já entrou.
 *
 * "sem valor definido" aparece porque reserva sem preço NÃO soma — e um
 * total que ignora três locações silenciosamente passa por fechamento do
 * dia quando não é.
 */
export function ResumoDeHorasACobrar({ dados }: { dados: HorasACobrar }) {
  const { totais } = dados;
  return (
    <div className="mini">
      <div>
        <b>{dinheiro(totais.aCobrar)}</b>
        <small>
          a cobrar · {horas(totais.minutosACobrar)}
          {totais.semValor > 0
            ? ` · ${totais.semValor} sem valor definido`
            : ''}
        </small>
      </div>
      <div>
        <b>{dinheiro(totais.recebido)}</b>
        <small>recebido · {horas(totais.minutosRecebidos)}</small>
      </div>
    </div>
  );
}

export function ListaDeHorasACobrar({
  dados,
  limite,
  vazio = 'Nenhuma hora a cobrar no período.',
  comRecebidas = false,
}: {
  dados: HorasACobrar;
  limite?: number;
  vazio?: string;
  /** Mostra também o que já foi recebido — o financeiro quer as duas pontas. */
  comRecebidas?: boolean;
}) {
  const { pode } = useSessao();
  const [baixando, setBaixando] = useState<string | null>(null);
  const [todas, setTodas] = useState(false);

  const itens = comRecebidas ? [...dados.aCobrar, ...dados.recebidas] : dados.aCobrar;
  const ordenados = [...itens].sort((a, b) => a.inicio.localeCompare(b.inicio));
  const mostrados = limite && !todas ? ordenados.slice(0, limite) : ordenados;
  const escondidos = ordenados.length - mostrados.length;

  const podeBaixar = pode('agenda', 'editar');
  const nomeDaQuadra = new Map(dados.quadras.map((q) => [q.id, q.nome]));

  if (ordenados.length === 0) return <Vazio>{vazio}</Vazio>;

  return (
    <>
      <Feed>
        {mostrados.map((hora) => (
          <ItemDeFeed
            key={hora.id}
            marca={
              <span
                className="sq"
                style={{ background: hora.pagoEm ? 'var(--green)' : 'var(--panel-3)' }}
              >
                <Icone nome={hora.pagoEm ? 'check' : 'money'} />
              </span>
            }
            titulo={`${horaCurta(horaNaArena(hora.inicio, dados.fuso))} às ${horaCurta(
              horaNaArena(hora.fim, dados.fuso),
            )} · ${hora.responsavelNome ?? hora.titulo ?? ROTULO_TIPO_MARCACAO[hora.tipo]}`}
            subtitulo={[
              nomeDaQuadra.get(hora.quadraId),
              // A data no fuso DA ARENA: cortar o ISO daria a data em UTC,
              // e a locação das 21h cairia no dia seguinte.
              dataCurta(partesNoFuso(hora.inicio, dados.fuso).data),
              horas(hora.minutos),
              hora.responsavelTelefone ? telefoneFormatado(hora.responsavelTelefone) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
            direita={
              <>
                <b>{hora.valor != null ? dinheiro(hora.valor) : 'sem valor'}</b>
                <small>
                  <SituacaoDaHora hora={hora} />
                </small>
              </>
            }
            {...(podeBaixar ? { onClick: () => setBaixando(hora.id) } : {})}
          />
        ))}
      </Feed>

      {escondidos > 0 && (
        <div style={{ marginTop: 10 }}>
          <Botao variante="fantasma" pequeno onClick={() => setTodas(true)}>
            Ver as outras {escondidos}
          </Botao>
        </div>
      )}

      {baixando && (
        <ModalDeBaixa
          marcacaoId={baixando}
          hoje={hojeNoFuso(dados.fuso)}
          fuso={dados.fuso}
          onFechar={() => setBaixando(null)}
        />
      )}
    </>
  );
}

function SituacaoDaHora({ hora }: { hora: HoraACobrar }) {
  if (hora.situacaoPagamento === 'Pago') {
    return <Tag>{hora.pagoEm ? `pago em ${dataCurta(hora.pagoEm)}` : 'pago'}</Tag>;
  }
  if (hora.situacaoPagamento === 'Sinal') return <Status situacao="atencao">sinal</Status>;
  if (hora.situacaoPagamento === 'APagar') return <Status situacao="aguardando">a pagar</Status>;
  return <Status situacao="neutra">a definir</Status>;
}
